import {useCallback, useMemo, useState} from 'react';

import type {EmailDeliverabilityFragment as EmailDeliverabilityStatus} from 'nutshell-graphql-types';

import {ConnectButtonsRow} from '../../../../../../ui/core-entity/rows/peep-rows/connect-buttons-row';
import type {UserFacingEntity} from '../../../../../../ui/core-entity/rows/peep-rows/types';

import {EditableTextfieldContent} from '../../editable-textfield-content/editable-textfield-content';
import type {SelectMenuOption} from '../../select-menu/select-menu';

import {BaseRow} from '../base-row/base-row';
import {getBusinessCardRowToolbarActions} from '../base-row/helpers';
import {RenameableRowTitle} from '../base-row/renameable-row-title';
import {useDeleteConfirmation} from '../base-row/use-delete-confirmation';
import {useValueSaveToastTracker} from '../base-row/use-value-save-toast-tracker';

interface Props {
    title?: string;
    email: string;
    parentEntity: UserFacingEntity;
    isPrimary?: boolean;
    showIsPrimaryIcon?: boolean;
    isFormer?: boolean;
    canMarkAsPrimary?: boolean;
    dropdownOptions?: SelectMenuOption[];
    onUpdateEmail: (email: string) => Promise<any>;
    onMarkAsPrimary?: () => Promise<any>;
    onChangeFormerStatus?: (isFormer: boolean) => Promise<any>;
    onDelete: () => Promise<any>;
    onUpdateTitle: (title: string) => Promise<any>;
    successFlashKey?: string;
    connectButtonsProps?: {
        entityId: string;
        sendFromNutshellEnabled: boolean | null | undefined;
        onOpenEmailModal: (emailAddress: string) => void;
        deliverabilityStatus?: EmailDeliverabilityStatus | null | undefined;
    };
}

export function EmailRow(props: Props) {
    const {onUpdateEmail, isFormer, showIsPrimaryIcon = true} = props;

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isMutatingToolbar, setIsMutatingToolbar] = useState<boolean>(false);
    const [isTitleDropdownOpen, setIsTitleDropdownOpen] = useState<boolean>(false);
    const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
    const [isCopied, setIsCopied] = useState<boolean>(false);

    const {requestDelete, isDeleteConfirmOpen, deleteConfirmationDialog, deleteAnchorRef} =
        useDeleteConfirmation(props.onDelete, setIsMutatingToolbar, 'email', props.parentEntity);

    const {valueSaveToastState, setValueSaveToastState, trackValueSaveToastForPromise} =
        useValueSaveToastTracker();

    const handleRowClick = useCallback(() => {
        if (isEditing || isFormer) {
            return;
        }

        setIsEditing(true);
    }, [isEditing, isFormer]);

    const handleUpdate = useCallback(
        (newValue?: string | null | undefined) => {
            const formatted = newValue ? newValue.trim() : '';
            return trackValueSaveToastForPromise(
                onUpdateEmail(formatted).then((result) => {
                    setIsEditing(false);
                    return result;
                })
            );
        },
        [onUpdateEmail, trackValueSaveToastForPromise]
    );

    const toolbarActions = useMemo(() => {
        return getBusinessCardRowToolbarActions(
            {
                isFormer: props.isFormer,
                canMarkAsPrimary: props.canMarkAsPrimary && !props.isPrimary,
                canChangeFormerStatus: !!props.onChangeFormerStatus,
            },
            {
                onMarkAsPrimary: props.onMarkAsPrimary,
                onChangeFormerStatus: props.onChangeFormerStatus,
                onEdit: () => setIsEditing(true),
                onDelete: requestDelete,
                onDeleteRef: deleteAnchorRef,
                onCopy: (isCopied: boolean) => setIsCopied(isCopied),
            },
            props.email,
            isCopied,
            setIsMutatingToolbar
        );
    }, [
        props.isPrimary,
        props.isFormer,
        props.canMarkAsPrimary,
        props.onMarkAsPrimary,
        props.onChangeFormerStatus,
        requestDelete,
        deleteAnchorRef,
        props.email,
        isCopied,
    ]);

    return (
        <>
            <BaseRow
                type='email'
                onClick={handleRowClick}
                successFlashKey={props.successFlashKey}
                isPrimary={showIsPrimaryIcon ? props.isPrimary : undefined}
                toolbarProps={{
                    ...toolbarActions,
                    forceShowToolbar: isDeleteConfirmOpen,
                    forceHideToolbar:
                        isEditing || isMutatingToolbar || isTitleDropdownOpen || isEditingTitle,
                }}
            >
                <RenameableRowTitle
                    title={props.title ?? 'Email'}
                    options={props.dropdownOptions ?? []}
                    onUpdateTitle={props.onUpdateTitle}
                    saveToastState={valueSaveToastState}
                    setSaveToastState={setValueSaveToastState}
                    onDropdownOpenChange={setIsTitleDropdownOpen}
                    setIsEditingTitle={setIsEditingTitle}
                    isCopied={isCopied}
                />
                <EditableTextfieldContent
                    value={props.email}
                    displayValue={props.email}
                    onUpdate={handleUpdate}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    placeholder='Add email…'
                    preventHideOnBlur={true}
                    noTopMargin={true}
                    ignorePasswordAutoFill={true}
                />
                {props.connectButtonsProps && !isEditing && !isFormer && (
                    <ConnectButtonsRow
                        id={props.connectButtonsProps.entityId}
                        email={props.email}
                        sendFromNutshellEnabled={props.connectButtonsProps.sendFromNutshellEnabled}
                        onOpenEmailModal={props.connectButtonsProps.onOpenEmailModal}
                        deliverabilityStatus={props.connectButtonsProps.deliverabilityStatus}
                    />
                )}
            </BaseRow>
            {deleteConfirmationDialog}
        </>
    );
}
